import { Area } from "@ant-design/plots";
import {
    AlertTriangle,
    ArrowDownRight,
    ArrowRight,
    ArrowUpRight,
    ChevronRight,
    CircleDollarSign,
    Cloud,
    Lightbulb,
    LoaderCircle,
    RefreshCw,
    RotateCcw,
    Tags,
    TrendingUp,
    XCircle
} from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import type {
    CostExplorerBreakdownItem,
    CostExplorerGroupBy,
    CostExplorerInsight,
    CostExplorerPeriodPreset
} from "../../backend/cost-optimizer";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { AWS_REGIONS } from "../../enums/enums";
import { currentProjectState } from "../../recoil/project";
import CostPageHeader from "./components/CostPageHeader";
import { useCostExplorer, useCostExplorerMetadata } from "./hooks/useCostExplorer";

type BreakdownKey = CostExplorerGroupBy;

interface CostExplorerMockupProps {
    navigation?: React.ReactNode;
    onOpenOptimizer: () => void;
}

interface DisplayBreakdownItem extends CostExplorerBreakdownItem {
    color: string;
}

const filterLabels: Record<BreakdownKey, string> = {
    service: "Service",
    environment: "Environment",
    account: "Account",
    region: "Region"
};

const chartColors = ["#087bb5", "#4f93b9", "#7aa6aa", "#c7a45d", "#8894a3", "#b1bac4", "#6f83a0"];

function errorMessage(error: unknown) {
    const candidate = error as { response?: { data?: { message?: string } }; message?: string };
    return candidate.response?.data?.message || candidate.message || "The request failed. Try again.";
}

function dateValue(value?: string) {
    if (!value) return undefined;
    const parsed = new Date(value.includes("T") ? value : `${value}T00:00:00`);
    return Number.isNaN(parsed.valueOf()) ? undefined : parsed;
}

function formatDate(value?: string) {
    const date = dateValue(value);
    return date ? new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date) : "Unknown date";
}

function formatRange(start?: string, end?: string) {
    const inclusiveEnd = dateValue(end);
    if (inclusiveEnd) inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
    return `${formatDate(start)} to ${inclusiveEnd
        ? new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(inclusiveEnd)
        : "Unknown date"}`;
}

function formatMoney(value: number, currency: string) {
    return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency || "USD",
        maximumFractionDigits: Math.abs(value) < 100 ? 2 : 0
    }).format(value);
}

function formatPercent(value: number) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(Math.abs(value));
}

export default function CostExplorerMockup({ navigation, onOpenOptimizer }: CostExplorerMockupProps) {
    const { projectId = "" } = useParams<{ projectId: string }>();
    const currentProject = useRecoilValue(currentProjectState);
    const costExplorerEnabled = currentProject?.paymentPlan?.limitations?.cloudCostOptimizer !== false;
    const [period, setPeriod] = useState<CostExplorerPeriodPreset>("month-to-date");
    const [account, setAccount] = useState("all");
    const [environment, setEnvironment] = useState("all");
    const [region, setRegion] = useState("all");
    const [service, setService] = useState("all");
    const [viewBy, setViewBy] = useState<BreakdownKey>("service");
    const [knownServices, setKnownServices] = useState<string[]>([]);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    const metadataQuery = useCostExplorerMetadata(projectId, costExplorerEnabled);
    const accounts = useMemo(() => metadataQuery.data?.accounts || [], [metadataQuery.data]);
    const environments = useMemo(() => metadataQuery.data?.environments || [], [metadataQuery.data]);
    const accountIds = useMemo(
        () => account === "all" ? accounts.map(item => item.id) : [account],
        [account, accounts]
    );
    const filters = useMemo(() => ({
        accountIds,
        period,
        groupBy: viewBy,
        ...(environment !== "all" ? { environmentId: environment } : {}),
        ...(region !== "all" ? { region } : {}),
        ...(service !== "all" ? { service } : {})
    }), [accountIds, period, viewBy, environment, region, service]);
    const costQuery = useCostExplorer(
        projectId,
        filters,
        costExplorerEnabled && metadataQuery.isSuccess && accounts.length > 0
    );
    const data = costQuery.data;

    useEffect(() => {
        if (!data?.serviceOptions?.length) return;
        setKnownServices(current => Array.from(new Set([...current, ...data.serviceOptions])));
    }, [data?.serviceOptions]);

    useEffect(() => {
        setExpandedRows(new Set());
    }, [period, account, environment, region, service, viewBy]);

    const accountNames = useMemo(
        () => new Map(accounts.map(item => [item.id, item.accountName || item.id])),
        [accounts]
    );
    const environmentNames = useMemo(
        () => new Map(environments.map(item => [item.id, item.name || item.id])),
        [environments]
    );
    const regionNames = useMemo(
        () => new Map(AWS_REGIONS.map(item => [item.value, item.name])),
        []
    );

    const displayName = useCallback((key: BreakdownKey, id: string, fallback: string) => {
        if (key === "account") return accountNames.get(id) || fallback || id;
        if (key === "environment") return environmentNames.get(id) || fallback || id;
        if (key === "region") return regionNames.get(id) || fallback || id;
        return fallback || id;
    }, [accountNames, environmentNames, regionNames]);

    const groupColors = useMemo(() => {
        const ids = new Set<string>();
        data?.breakdown.forEach(item => ids.add(item.id));
        data?.timeSeries.forEach(point => point.groups.forEach(group => ids.add(group.id)));
        return new Map(Array.from(ids).map((id, index) => [id, chartColors[index % chartColors.length]]));
    }, [data]);

    const visibleRows = useMemo<DisplayBreakdownItem[]>(() => (data?.breakdown || []).map((item, index) => ({
        ...item,
        name: displayName(viewBy, item.id, item.name),
        color: groupColors.get(item.id) || chartColors[index % chartColors.length]
    })), [data?.breakdown, displayName, groupColors, viewBy]);

    const chartData = useMemo(() => (data?.timeSeries || []).flatMap(point => {
        const groups = point.groups.length ? point.groups : [{ id: "total", name: "Total", cost: point.total }];
        return groups.map(group => ({
            date: point.date,
            cost: group.cost,
            group: displayName(viewBy, group.id, group.name),
            groupId: group.id,
            estimated: point.estimated
        }));
    }), [data?.timeSeries, displayName, viewBy]);

    const clearFilters = () => {
        setPeriod("month-to-date");
        setAccount("all");
        setEnvironment("all");
        setRegion("all");
        setService("all");
    };

    const selectAccount = (value: string) => {
        setAccount(value);
        if (environment !== "all") {
            const selectedEnvironment = environments.find(item => item.id === environment);
            if (value !== "all" && selectedEnvironment?.awsAccountId !== value) setEnvironment("all");
        }
    };

    const focusBreakdown = (value: string) => {
        if (viewBy === "account") selectAccount(value);
        if (viewBy === "environment") setEnvironment(value);
        if (viewBy === "region") setRegion(value);
        if (viewBy === "service") setService(value);
        window.setTimeout(() => document.getElementById("cost-breakdown")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
    };

    const toggleBreakdownRow = (id: string) => {
        setExpandedRows(current => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const chartConfig: any = {
        data: chartData,
        xField: "date",
        yField: "cost",
        seriesField: "group",
        isStack: true,
        smooth: true,
        height: 276,
        animation: false,
        color: Array.from(new Set(chartData.map(item => item.groupId))).map(id => groupColors.get(id) || chartColors[0]),
        areaStyle: { fillOpacity: 0.18 },
        line: { style: { lineWidth: 2 } },
        legend: { position: "top-right" },
        xAxis: {
            tickCount: 7,
            label: { formatter: (value: string) => formatDate(value) }
        },
        yAxis: { label: { formatter: (value: string) => formatMoney(Number(value), data?.currency || "USD") } },
        tooltip: {
            formatter: (datum: { group: string; cost: number; estimated: boolean }) => ({
                name: `${datum.group}${datum.estimated ? " (estimated)" : ""}`,
                value: formatMoney(datum.cost, data?.currency || "USD")
            })
        }
    };

    const pageHeader = (
        <CostPageHeader
            title="Cost explorer"
            description="See what changed, what caused it, and where to act."
            icon={<CircleDollarSign size={21} />}
            navigation={navigation}
        />
    );

    if (!costExplorerEnabled) {
        return (
            <div className="cost-explorer-shell">
                {pageHeader}
                <CenteredState
                    icon={<CircleDollarSign size={34} />}
                    title="Cost explorer is not included in this plan"
                    detail="Change the project plan to view AWS cost history and forecasts."
                    action={<Button asChild><Link to={`/projects/${projectId}/settings/billing`}>View plans</Link></Button>}
                />
            </div>
        );
    }

    if (metadataQuery.isLoading) {
        return <div className="cost-explorer-shell">{pageHeader}<CostExplorerSkeleton /></div>;
    }

    if (metadataQuery.error) {
        return (
            <div className="cost-explorer-shell">
                {pageHeader}
                <CenteredState
                    icon={<XCircle size={34} />}
                    title="Could not load AWS accounts"
                    detail={errorMessage(metadataQuery.error)}
                    action={<Button onClick={() => metadataQuery.refetch()}><RefreshCw size={15} /> Try again</Button>}
                />
            </div>
        );
    }

    if (accounts.length === 0) {
        return (
            <div className="cost-explorer-shell">
                {pageHeader}
                <CenteredState
                    icon={<Cloud size={34} />}
                    title="Connect an AWS account to see costs"
                    detail="Cost data appears here after Microtica can read billing data from AWS."
                    action={<Button asChild><Link to={`/projects/${projectId}/integrations/cloud`}>Connect AWS account</Link></Button>}
                />
            </div>
        );
    }

    if (costQuery.isLoading) {
        return <div className="cost-explorer-shell">{pageHeader}<CostExplorerSkeleton /></div>;
    }

    if (costQuery.error || !data) {
        return (
            <div className="cost-explorer-shell">
                {pageHeader}
                <CenteredState
                    icon={<XCircle size={34} />}
                    title="Could not load cost data"
                    detail={errorMessage(costQuery.error)}
                    action={<Button onClick={() => costQuery.refetch()}><RefreshCw size={15} /> Try again</Button>}
                />
            </div>
        );
    }

    const hasFilters = [account, environment, region, service].some(value => value !== "all") || period !== "month-to-date";
    const hasCostData = data.breakdown.length > 0 || data.timeSeries.length > 0 || data.summary.currentCost !== 0;
    const comparisonDirection = data.summary.changeAmount < 0 ? "lower" : data.summary.changeAmount > 0 ? "higher" : "unchanged";
    const hasMeaningfulComparison = Math.abs(data.summary.comparisonCost) >= 0.01;
    const availableEnvironments = environments.filter(item => account === "all" || item.awsAccountId === account);

    return (
        <div className="cost-explorer-shell">
            {pageHeader}

            <main className="cost-explorer">
                <Card className="cost-filters">
                    <CardContent>
                        <div className="cost-filters__meta">
                            <span>Cost data</span>
                            <div>
                                {costQuery.isFetching && <Badge variant="secondary"><LoaderCircle className="cost-spin" size={13} /> Updating</Badge>}
                                <Badge variant="outline" title={`Generated ${data.generatedAt}`}>Through {formatDate(data.period.dataThrough)}</Badge>
                                <Badge variant="positive">After AWS discounts</Badge>
                                {data.period.estimated && <Badge variant="warning">Includes estimates</Badge>}
                            </div>
                        </div>
                        <div className="cost-filters__controls">
                            <Filter label="Period" value={period} onChange={value => setPeriod(value as CostExplorerPeriodPreset)} options={[
                                ["month-to-date", "This month so far"],
                                ["last-month", "Last month"]
                            ]} />
                            <Filter label="Account" value={account} onChange={selectAccount} options={[
                                ["all", "All accounts"],
                                ...accounts.map(item => [item.id, item.accountName || item.id])
                            ]} />
                            <Filter label="Environment" value={environment} onChange={setEnvironment} options={[
                                ["all", "All environments"],
                                ...availableEnvironments.map(item => [item.id, item.name])
                            ]} />
                            <Filter label="Region" value={region} onChange={setRegion} options={[
                                ["all", "All regions"],
                                ...AWS_REGIONS.map(item => [item.value, item.name])
                            ]} />
                            <Filter label="Service" value={service} onChange={setService} options={[
                                ["all", "All services"],
                                ...knownServices.map(item => [item, item])
                            ]} />
                            <Button variant="ghost" size="sm" disabled={!hasFilters} onClick={clearFilters}>
                                <RotateCcw size={14} /> Clear
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {data.warnings.length > 0 && (
                    <div className="cost-warning" role="status">
                        <AlertTriangle size={17} />
                        <div>
                            <strong>Some account costs are unavailable</strong>
                            {data.warnings.map(warning => (
                                <span key={`${warning.accountId}-${warning.message}`}>
                                    {accountNames.get(warning.accountId) || warning.accountId}: {warning.message}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {!hasCostData ? (
                    <Card className="cost-empty-state">
                        <CardContent>
                            <CircleDollarSign size={30} />
                            <strong>No cost data for these filters</strong>
                            <span>Clear the filters or choose another period.</span>
                            {hasFilters && <Button variant="outline" onClick={clearFilters}>Clear filters</Button>}
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        <section className="cost-summary" aria-label="Cost summary">
                            <SummaryCard
                                icon={<CircleDollarSign size={18} />}
                                label={period === "last-month" ? "Spend last month" : "Current spend"}
                                value={formatMoney(data.summary.currentCost, data.currency)}
                                detail={formatRange(data.period.start, data.period.end)}
                            />
                            <SummaryCard
                                icon={<TrendingUp size={18} />}
                                label={period === "last-month" ? "Average each day" : "Expected this month"}
                                value={period === "last-month"
                                    ? formatMoney(data.summary.dailyAverage, data.currency)
                                    : data.summary.projectedMonthEnd === null
                                        ? "Not available"
                                        : formatMoney(data.summary.projectedMonthEnd, data.currency)}
                                detail={period === "last-month"
                                    ? "Across the selected month"
                                    : data.summary.projectedMonthEnd === null
                                        ? "AWS has not returned a forecast"
                                        : "Based on the current daily pace"}
                            />
                            <SummaryCard
                                icon={data.summary.changeAmount < 0
                                    ? <ArrowDownRight size={18} />
                                    : data.summary.changeAmount > 0
                                        ? <ArrowUpRight size={18} />
                                        : <ArrowRight size={18} />}
                                label="Change from comparison period"
                                value={`${data.summary.changeAmount > 0 ? "+" : data.summary.changeAmount < 0 ? "-" : ""}${formatMoney(Math.abs(data.summary.changeAmount), data.currency)}`}
                                detail={!hasMeaningfulComparison || data.summary.changePercent === null
                                    ? data.summary.currentCost >= 0.01
                                        ? "New spend compared with the previous period"
                                        : "No earlier spend to compare"
                                    : comparisonDirection === "unchanged"
                                    ? `No change from ${formatRange(data.period.comparisonStart, data.period.comparisonEnd)}`
                                    : `${formatPercent(data.summary.changePercent)}% ${comparisonDirection}`}
                                tone={data.summary.changeAmount < 0 ? "positive" : data.summary.changeAmount > 0 ? "warning" : "default"}
                            />
                            <SummaryCard
                                icon={<Tags size={18} />}
                                label="Matched to environments"
                                value={`${formatPercent(data.summary.matchedPercent)}%`}
                                detail={data.summary.unmatchedCost === 0
                                    ? "All selected costs have an environment"
                                    : `${formatMoney(data.summary.unmatchedCost, data.currency)} could not be matched`}
                                tone={data.summary.unmatchedCost === 0 ? "positive" : "warning"}
                            />
                        </section>

                        <div className="cost-explorer__grid">
                            <Card className="cost-trend-card">
                                <CardHeader>
                                    <div>
                                        <CardTitle>Spend over the selected period</CardTitle>
                                        <CardDescription>{formatRange(data.period.start, data.period.end)} · grouped by {filterLabels[viewBy].toLowerCase()}</CardDescription>
                                    </div>
                                    <Tabs value={viewBy} onValueChange={value => setViewBy(value as BreakdownKey)}>
                                        <TabsList aria-label="Group spend">
                                            <TabsTrigger value="service">Service</TabsTrigger>
                                            <TabsTrigger value="environment">Environment</TabsTrigger>
                                            <TabsTrigger value="account">Account</TabsTrigger>
                                            <TabsTrigger value="region">Region</TabsTrigger>
                                        </TabsList>
                                    </Tabs>
                                </CardHeader>
                                <CardContent>
                                    {chartData.length > 0
                                        ? <Area {...chartConfig} />
                                        : <InlineEmpty message="No spend history for this period" />}
                                </CardContent>
                            </Card>

                            <Card className="cost-insights-card">
                                <CardHeader>
                                    <div className="cost-insights-card__heading">
                                        <span><Lightbulb size={17} /></span>
                                        <div>
                                            <CardTitle>Cost insights</CardTitle>
                                            <CardDescription>Based on the selected accounts and filters</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {data.insights.length > 0
                                        ? data.insights.map(insight => (
                                            <Insight
                                                key={insight.id}
                                                insight={insight}
                                                onClick={insight.groupId ? () => focusBreakdown(insight.groupId!) : undefined}
                                            />
                                        ))
                                        : <InlineEmpty message="No cost insights for these filters" />}
                                    <Button className="cost-schedules-link" variant="ghost" size="sm" onClick={onOpenOptimizer}>
                                        View resource schedules <ArrowRight size={14} />
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>

                        <Card className="cost-breakdown-card" id="cost-breakdown">
                            <CardHeader>
                                <div>
                                    <CardTitle>Cost breakdown</CardTitle>
                                    <CardDescription>Where the selected spend came from</CardDescription>
                                </div>
                                <Badge variant="outline">Grouped by {filterLabels[viewBy].toLowerCase()}</Badge>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>{filterLabels[viewBy]}</TableHead>
                                            <TableHead>% of selected spend</TableHead>
                                            <TableHead className="cost-table-number">Cost</TableHead>
                                            <TableHead className="cost-table-number">Change</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {visibleRows.map(item => {
                                            const children = item.children || [];
                                            const hasChildren = children.length > 0;
                                            const isExpanded = hasChildren && expandedRows.has(item.id);
                                            const childLabel = viewBy === "service" ? "Cost driver" : "Service";
                                            const childRegionId = `cost-breakdown-children-${item.id.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

                                            return (
                                                <Fragment key={item.id}>
                                                    <TableRow className={isExpanded ? "cost-parent-row cost-parent-row--expanded" : "cost-parent-row"}>
                                                        <TableCell>
                                                            <span className="cost-table-name">
                                                                {hasChildren
                                                                    ? <Button
                                                                        aria-controls={childRegionId}
                                                                        aria-expanded={isExpanded}
                                                                        aria-label={`${isExpanded ? "Hide" : "Show"} ${childLabel.toLowerCase()} breakdown for ${item.name}`}
                                                                        className="cost-expand-button"
                                                                        onClick={() => toggleBreakdownRow(item.id)}
                                                                        size="icon"
                                                                        type="button"
                                                                        variant="ghost"
                                                                    >
                                                                        <ChevronRight aria-hidden="true" size={15} />
                                                                    </Button>
                                                                    : <span aria-hidden="true" className="cost-expand-spacer" />}
                                                                <span className="cost-table-color" style={{ background: item.color }} />
                                                                <span>{item.name}</span>
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="cost-share">
                                                                <div><span style={{ width: `${Math.max(0, Math.min(100, item.share))}%`, background: item.color }} /></div>
                                                                <span>{formatPercent(item.share)}%</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="cost-table-number"><strong>{formatMoney(item.cost, data.currency)}</strong></TableCell>
                                                        <TableCell className="cost-table-number">
                                                            <Change
                                                                value={item.changePercent}
                                                                currentCost={item.cost}
                                                                comparisonCost={item.comparisonCost}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                    {isExpanded && (
                                                        <TableRow className="cost-child-row">
                                                            <TableCell colSpan={4}>
                                                                <div className="cost-child-breakdown" id={childRegionId}>
                                                                    <div className="cost-child-breakdown__heading">
                                                                        <strong>{viewBy === "service" ? `Cost drivers for ${item.name}` : `Services in ${item.name}`}</strong>
                                                                        <span>{children.length} {children.length === 1 ? "item" : "items"}</span>
                                                                    </div>
                                                                    <Table className="cost-child-table">
                                                                        <TableHeader>
                                                                            <TableRow>
                                                                                <TableHead>{childLabel}</TableHead>
                                                                                <TableHead>% of this row</TableHead>
                                                                                <TableHead className="cost-table-number">Cost</TableHead>
                                                                                <TableHead className="cost-table-number">Change</TableHead>
                                                                            </TableRow>
                                                                        </TableHeader>
                                                                        <TableBody>
                                                                            {children.map(child => (
                                                                                <TableRow key={`${item.id}-${child.id}`}>
                                                                                    <TableCell>
                                                                                        <span className="cost-child-name">{child.name}</span>
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        <div className="cost-share cost-share--child">
                                                                                            <div><span style={{ width: `${Math.max(0, Math.min(100, child.shareOfParent))}%`, background: item.color }} /></div>
                                                                                            <span>{formatPercent(child.shareOfParent)}%</span>
                                                                                        </div>
                                                                                    </TableCell>
                                                                                    <TableCell className="cost-table-number"><strong>{formatMoney(child.cost, data.currency)}</strong></TableCell>
                                                                                    <TableCell className="cost-table-number">
                                                                                        <Change
                                                                                            value={child.changePercent}
                                                                                            currentCost={child.cost}
                                                                                            comparisonCost={child.comparisonCost}
                                                                                        />
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </>
                )}
            </main>
        </div>
    );
}

function Filter({ label, value, onChange, options }: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: string[][];
}) {
    return (
        <label className="cost-filter">
            <span>{label}</span>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger aria-label={label}>
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {options.map(([optionValue, optionLabel]) => (
                        <SelectItem key={optionValue} value={optionValue}>{optionLabel}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </label>
    );
}

function SummaryCard({ icon, label, value, detail, tone = "default" }: {
    icon: React.ReactNode;
    label: string;
    value: string;
    detail: string;
    tone?: "default" | "warning" | "positive";
}) {
    return (
        <Card className={`cost-summary-card cost-summary-card--${tone}`}>
            <CardContent>
                <div className="cost-summary-card__label"><span>{icon}</span>{label}</div>
                <strong>{value}</strong>
                <small>{detail}</small>
            </CardContent>
        </Card>
    );
}

function Insight({ insight, onClick }: { insight: CostExplorerInsight; onClick?: () => void }) {
    const tone = insight.tone === "warning" || insight.tone === "positive" ? insight.tone : "default";
    return (
        <article className={`cost-insight cost-insight--${tone}`}>
            <span className="cost-insight__marker" />
            <div>
                <strong>{insight.title}</strong>
                <p>{insight.body}</p>
                {onClick && (
                    <Button variant="ghost" size="sm" onClick={onClick}>
                        Show in breakdown <ArrowRight size={14} />
                    </Button>
                )}
            </div>
        </article>
    );
}

function Change({ value, currentCost, comparisonCost }: {
    value: number | null;
    currentCost: number;
    comparisonCost: number;
}) {
    if (Math.abs(comparisonCost) < 0.01 || value === null) {
        return <span className="cost-change">{currentCost >= 0.01 ? "New spend" : "No earlier spend"}</span>;
    }
    if (value === 0) return <span className="cost-change">No change</span>;
    return (
        <span className={value < 0 ? "cost-change cost-change--down" : "cost-change cost-change--up"}>
            {value < 0 ? <ArrowDownRight size={14} /> : <ArrowUpRight size={14} />}
            {value < 0 ? "Down" : "Up"} {formatPercent(value)}%
        </span>
    );
}

function InlineEmpty({ message }: { message: string }) {
    return <div className="cost-inline-empty">{message}</div>;
}

function CenteredState({ icon, title, detail, action }: {
    icon: React.ReactNode;
    title: string;
    detail: string;
    action?: React.ReactNode;
}) {
    return (
        <main className="cost-centered-state">
            <span>{icon}</span>
            <h2>{title}</h2>
            <p>{detail}</p>
            {action}
        </main>
    );
}

function CostExplorerSkeleton() {
    return (
        <main className="cost-explorer cost-explorer-loading" aria-label="Loading cost data">
            <Card className="cost-filters"><CardContent><Skeleton className="cost-skeleton-filter" /></CardContent></Card>
            <section className="cost-summary">
                {[0, 1, 2, 3].map(item => <Card className="cost-summary-card" key={item}><CardContent><Skeleton className="cost-skeleton-summary" /></CardContent></Card>)}
            </section>
            <div className="cost-explorer__grid">
                <Card><CardContent><Skeleton className="cost-skeleton-chart" /></CardContent></Card>
                <Card><CardContent><Skeleton className="cost-skeleton-chart" /></CardContent></Card>
            </div>
            <Card className="cost-breakdown-card"><CardContent><Skeleton className="cost-skeleton-table" /></CardContent></Card>
        </main>
    );
}
